import React, {useState, useEffect, useCallback, useRef} from 'react';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {useTranslation} from 'react-i18next';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import {SystemLogsFilterSchema, SystemLogsSeverities} from '../../../api/DBSchema/SystemLogs';


function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function SystemLogsFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );
    
    const [isGlobalScope, setIsGlobalScope] = useState(false);

    const [origin, setOrigin] = useState(null);
    const [user, setUser] = useState(null);
    const [minSeverity, setMinSeverity] = useState(SystemLogsSeverities.notice);
    
    const [linesLimit, setLinesLimit] = useState(10000);
        
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        
        setIsGlobalScope(false);

        setOrigin(null);
        setUser(null);

        setMinSeverity(SystemLogsSeverities.notice);
        setLinesLimit(10000);

        setStartDate(null);
        setEndDate(null);
    }, []);


    // Update filter request object on value change
    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};

        if (freeTextSearch) {
            filterRequestObj[SystemLogsFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }

        filterRequestObj[SystemLogsFilterSchema.isGlobalScope] = { value: isGlobalScope };
        
        if (origin) {
            filterRequestObj[SystemLogsFilterSchema.origin] = { value: origin };
        }

        if (user) {
            filterRequestObj[SystemLogsFilterSchema.user] = { value: user };
        }
        
        filterRequestObj[SystemLogsFilterSchema.minSeverity] = { value: minSeverity };
        
        if (linesLimit !== -1) {
            filterRequestObj[SystemLogsFilterSchema.linesLimit] = { value: linesLimit };
        }

        if (startDate) {
            filterRequestObj[SystemLogsFilterSchema.startDate] = { value: startDate.getTime() };
        }

        if (endDate) {
            // End date is inclusive - set as start of day after
            const dayAfterEndDate = new Date(endDate);
            dayAfterEndDate.setUTCDate(endDate.getUTCDate() + 1);
            filterRequestObj[SystemLogsFilterSchema.endDate] = { value: dayAfterEndDate.getTime() };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange,
        isGlobalScope, origin,
        user, minSeverity, linesLimit,
        startDate, endDate,
    ]);


    return (
        <FilterLayout clearAllAction={resetToDefaults}>
            <SMCTextInput
                width="auto"
                height={26}
                placeholder="Search"
                value={freeTextSearch}
                innerRef={freeTextSearchRef}
                onChange={e => setFreeTextSearch(e.target.value)}
            />
            {/* General tab */}
            <SMCExpansionPanel title={t('SMP.GENERAL')}>
                <SMCSelect
                    value={isGlobalScope ? 'GLOBAL' : 'LOCAL'}
                    onChange={e => setIsGlobalScope(e.target.value === 'GLOBAL')}
                    label={t('SMC_LOGFILTER_SCOPE')}
                    labelWidth={60}
                    width={130}
                >
                    <SMCOption value="LOCAL">{t('SMC_LOGFILTER_THIS_PORTAL_SCOPE')}</SMCOption>
                    <SMCOption value="GLOBAL">{t('SMC_LOGFILTER_All_PORTALS_SCOPE')}</SMCOption>
                </SMCSelect>
                <SMCTextInput
                    label={t('SMC_LOGFILTER_ORIGIN')}
                    value={origin}
                    onChange={e => setOrigin(e.target.value)}
                    labelWidth={60}
                    width={130}
                    style={{ margin: 0 }}
                />
                <SMCTextInput
                    label={t('SMC_LOGFILTER_USER')}
                    value={user}
                    onChange={e => setUser(e.target.value)}
                    labelWidth={60}
                    width={130}
                    style={{ margin: 0 }}
                />
                <SMCSelect
                    value={minSeverity}
                    onChange={e => setMinSeverity(parseInt(e.target.value))}
                    label={t('SMC_LOGFILTER_SEVERITY')}
                    labelWidth={60}
                    width={130}
                >
                    <SMCOption value={SystemLogsSeverities.emergency}>{t('SMC_LOGPOLICY_FSEVER_EMERGENCY')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.alert}>{t('SMC_LOGPOLICY_FSEVER_ALERT')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.critical}>{t('SMC_LOGPOLICY_FSEVER_CRITICAL')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.error}>{t('SMC_LOGPOLICY_FSEVER_ERROR')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.warn}>{t('SMC_LOGPOLICY_FSEVER_WARNING')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.notice}>{t('SMC_LOGPOLICY_FSEVER_NOTICE')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.info}>{t('SMC_LOGPOLICY_FSEVER_INFO')}</SMCOption>
                    <SMCOption value={SystemLogsSeverities.debug}>{t('SMC_LOGPOLICY_FSEVER_DEBUG')}</SMCOption>
                </SMCSelect>                
                <SMCSelect
                    value={linesLimit}
                    onChange={e => setLinesLimit(parseInt(e.target.value))}
                    label={t('SMC_LOGFILTER_LIMIT')}
                    labelWidth={60}
                    width={130}
                >
                    <SMCOption value={-1}>{t('SMC_GEN_NONE')}</SMCOption>
                    <SMCOption value={100}>100</SMCOption>
                    <SMCOption value={1000}>1,000</SMCOption>
                    <SMCOption value={10000}>10,000</SMCOption>
                    <SMCOption value={100000}>100,000</SMCOption>
                </SMCSelect>
                
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={startDate}
                    toDate={endDate}
                    setFromDate={setStartDate}
                    setToDate={setEndDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(SystemLogsFilterPane);